import {
  COMBINED_HWW_CONST,
  CUMULATIVE_TRAFFIC_INTENSITY,
  HEALTH_INDEX_CONST,
  HOUSEHOLDS,
  NUMBER_OF_VEHICLES_AVAILABLE,
  PATIENT_TO_DENTIST_RATIO,
  PATIENT_TO_PHYSICIAN_RATIO,
  PATIENT_TO_THERAPIST_RATIO,
  POPULATION,
  trendLayers,
  WEALTH_INDEX_CONST,
  WISE_INDEX_CONST,
} from '../../../../shared/types/feature-data-type';
import { Polarity, polarityOf } from './popup-groups';

/**
 * A metric's distance from the national value, in the shape the popup draws:
 * a bar that grows out of a centre tick.
 */
export interface NationalDeviation {
  /** -1 (far below the country) … +1 (far above). 0 is the national value. */
  offset: number;
  /** Short badge, e.g. `+23%`, `2.4x`, `+1.2 pts`. */
  label: string;
  polarity: Polarity;
}

/**
 * How far from the national value counts as "the bar is full".
 *
 * Ratio metrics saturate at double or half the country, which covers the range
 * a US cell realistically lands in; anything past that reads as "off the
 * scale", which is what the `2.4x` badge is for.
 *
 * Measured on the *log* of the ratio, because that is the only scale on which
 * double and half sit the same distance from the country. The plain `ratio - 1`
 * this replaced filled the bar completely at 2x but only halfway at 0.5x, and
 * reached the far end of its own scale only at a ratio of zero — two cells
 * equally far from the US in ratio terms drew visibly different bars, on the
 * one device in the card whose whole job is being comparable between rows.
 *
 * Trends are percentage-point gaps and saturate at 3 points, because a county
 * growing 3 points a year faster than the country is already an outlier.
 */
const RATIO_SATURATION = Math.log(2);
const TREND_SATURATION_POINTS = 3;

/**
 * Metrics that must not be drawn against a national figure.
 *
 * Three separate reasons, all of them fatal to the bar:
 *
 *  - **Already a comparison.** The HWW indices *are* the ratio to the country;
 *    the hero grade says so, and a second bar would double-count it.
 *
 *  - **Extensive.** Population, households, the vehicle count and total daily
 *    vehicle-miles all grow with the size of the area. A 1-mile cell, a county
 *    and a state are three different questions, and dividing any of them by a
 *    single "US average" answers none. Their intensive siblings are comparable
 *    in principle and so are not listed here — but only the ones the backend
 *    publishes a country figure for actually draw a bar. See `nationalValueOf`.
 *
 *  - **Mismatched reference.** The country-level number the backend returns
 *    for the three provider-availability ratios is not the same quantity as
 *    the per-cell one, so the ratio came out meaningless on screen. If that is
 *    corrected server-side, deleting those three entries restores their bars.
 */
// Matched as a prefix, because each of these arrives with a scope suffix
// (`HWW_INDEX_by_country`, `HEALTH_INDEX_by_state`).
const NO_COMPARISON_PREFIXES = [
  COMBINED_HWW_CONST,
  HEALTH_INDEX_CONST,
  WEALTH_INDEX_CONST,
  WISE_INDEX_CONST,
];

// Matched exactly. `POPULATION` as a substring would also silence
// `POPULATION_GROWTH_TREND`, which is a rate and compares perfectly well.
const NO_COMPARISON_EXACT = [
  POPULATION,
  HOUSEHOLDS,
  NUMBER_OF_VEHICLES_AVAILABLE,
  CUMULATIVE_TRAFFIC_INTENSITY,

  PATIENT_TO_PHYSICIAN_RATIO,
  PATIENT_TO_DENTIST_RATIO,
  PATIENT_TO_THERAPIST_RATIO,
];

/**
 * Whether `feature` may be shown against the US at all — the bar *and* the
 * "US avg …" caption under it, which is just as wrong when the reference is.
 */
export function hasNationalComparison(feature: string): boolean {
  const key = feature.toUpperCase();
  return (
    !NO_COMPARISON_PREFIXES.some((f) => key.startsWith(f.toUpperCase())) &&
    !NO_COMPARISON_EXACT.some((f) => key === f.toUpperCase())
  );
}

export function nationalDeviation(
  feature: string,
  value: number | null,
  national: number | null
): NationalDeviation | null {
  if (value == null || national == null || !isFinite(value)) return null;
  if (!hasNationalComparison(feature)) return null;

  // Trends and any signed metric: a ratio of two numbers that straddle zero
  // says nothing, so compare the gap instead.
  if (trendLayers.includes(feature) || value * national < 0) {
    const points = value - national;
    if (Math.abs(points) < 0.05) return sameAsCountry();

    return {
      offset: clamp(points / TREND_SATURATION_POINTS),
      label: `${signed(points, 1)} pts`,
      polarity: polarityOf(feature, points > 0),
    };
  }

  if (national === 0) return null;

  const ratio = value / national;
  const percent = (ratio - 1) * 100;

  if (Math.round(percent) === 0) return sameAsCountry();

  return {
    offset: clamp(Math.log(ratio) / RATIO_SATURATION),
    label:
      ratio >= 2 || ratio <= 0.5
        ? `${trimZero(ratio)}x`
        : `${signed(percent, 0)}%`,
    polarity: polarityOf(feature, percent > 0),
  };
}

/**
 * Reads the national value ComparisonService cached for `feature`.
 *
 * Two shapes have to line up before the division is meaningful. The cache is
 * keyed by the uppercased feature name, because the popup's own properties
 * arrive lowercase from the tile server and uppercase from the backend. And
 * the values it holds are raw backend numbers, while a handful of features are
 * scaled by 100 on the way to the screen — the ratio cancels that out only if
 * neither side is scaled, so the *raw* cell value is what belongs here, not
 * the formatted one.
 *
 * Only `featuresToCompareToNationalPopup` (feature-data-type.ts) is ever
 * fetched and cached, so a feature outside that list returns null here and its
 * row renders with neither a bar nor a "US avg …" caption. The two density
 * rows are the standing example: `people_per_mi2` and `road_density_per_mi2`
 * are tile-server property names with no counterpart in the backend's
 * `AvailableFeatures`, so there is nothing to ask for. Adding them to the list
 * without a backend entry would be worse than the gap — every popup would
 * re-request the whole set forever, because the cache could never be filled.
 */
export function nationalValueOf(feature: string): number | null {
  const stored = localStorage.getItem(`${feature.toUpperCase()}_by_country`);
  if (stored == null || stored === '') return null;

  const parsed = Number(stored);
  return isFinite(parsed) ? parsed : null;
}

// The row already prints "US avg 77" as its reference, so the badge has to
// say the *relationship*, not repeat the reference back — "US avg 77 … US avg"
// read as a broken template.
function sameAsCountry(): NationalDeviation {
  return { offset: 0, label: 'same', polarity: 'neutral' };
}

function clamp(value: number): number {
  return Math.max(-1, Math.min(1, value));
}

function signed(value: number, digits: number): string {
  const rounded = value.toFixed(digits);
  // U+2212 MINUS SIGN: at 11px a hyphen next to tabular figures reads as a
  // hyphenated word rather than a negative number.
  return value > 0 ? `+${rounded}` : rounded.replace('-', '−');
}

function trimZero(ratio: number): string {
  return ratio.toFixed(1).replace(/\.0$/, '');
}
